import type React from "react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Settings,
  Upload,
  Users,
  X,
  LogOut,
  CircleUserRound,
  ChartCandlestick,
  Handshake,
  ShoppingCart,
  PackageOpen,
  Store,
  CalendarRange
} from "lucide-react";

import { Button } from "../features/ui/button";

import { ThemeToggle } from "../features/theme-toggle";
import { NotificationsDropdown } from "../features/notifications";
import { TenoreLogo } from "../features/TenoreLogo";
import { TenoreT } from "../features/TenoreT";
import { cn } from "../utils/utils";
import { useMobileMenu } from "../hooks/use-mobile-menu";
import { getUserInfo } from "../utils/getUserInfo";
import { useAuth } from '../features/auth/hooks/useAuth';
import MobileSidebar from "../components/MobileSidebar";

interface SubNavigationItem {
  icon: React.ReactNode;
  label: string;
  href: string;
  allowedRoles: string[];
}

interface NavigationItem {
  icon: React.ReactNode;
  label: string;
  href: string;
  allowedRoles: string[];
  subItems?: SubNavigationItem[];
}

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  href: string;
  collapsed: boolean;
  active?: boolean;
  onClick?: () => void;
  subItems?: SubNavigationItem[];
  userGroups: string[];
  expandedNav?: string | null; // add this
  setExpandedNav?: (href: string | null) => void; // add this
}

function NavItem({
  icon,
  label,
  href,
  collapsed,
  active,
  onClick,
  subItems,
  userGroups,
  expandedNav,
  setExpandedNav,
}: NavItemProps) {

  const location = useLocation();
  

  // Filter sub-items based on user permissions
  const allowedSubItems = subItems?.filter(subItem =>
    hasRouteAccess(userGroups, subItem.allowedRoles)
  ) || [];

  const hasSubItems = allowedSubItems.length > 0;

  // Check if any sub-item is currently active
  const isSubItemActive = allowedSubItems.some(subItem =>
    location.pathname === subItem.href
  );

  const isCurrentActive = active || isSubItemActive;

  const handleMainClick = (_e: React.MouseEvent) => {
    if (onClick) {
      onClick();
    }
  };
  // Remove local isExpanded state, and use expandedNav instead:
  const isExpanded = expandedNav === href;

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isExpanded) {
      setExpandedNav && setExpandedNav(null); // Close the dropdown
    } else {
      setExpandedNav && setExpandedNav(href); // Open the dropdown
    }
  };



  // For collapsed sidebar, update click handler:
  const getClickHandler = () => {
    if (collapsed && hasSubItems) {
      // If the sidebar is collapsed and the item has sub-items, toggle the dropdown
      return handleDropdownToggle;
    } else if (hasSubItems && !collapsed) {
      // If the sidebar is expanded and the item has sub-items, allow navigation to the main route
      return handleMainClick;
    } else {
      // Otherwise, handle the main click (navigate to the main item's link)
      return handleMainClick;
    }
  };

  

  return (
    <li className="relative">
      <div>
        <div className="flex items-center">
          {/* Main navigation link */}
          <Link
            to={href}
            className={cn(
              "flex items-center gap-2 rounded-md px-3.5 py-3 text-xs font-bold tracking-[.05em] uppercase transition-smooth",
              hasSubItems && !collapsed ? "flex-1" : "w-full",
              isCurrentActive
                ? "text-primary-foreground bg-primary"
                : "text-secondary-foreground"
            )}
            onClick={getClickHandler()} // Use the updated getClickHandler
          >
            <span className="flex-shrink-0">{icon}</span>
            {!collapsed && <span className="flex-1">{label}</span>}
            {/* Only show chevron when sidebar is expanded and has sub-items */}
          </Link>

          {/* Dropdown toggle button (only when expanded and has sub-items) */}
          {hasSubItems && !collapsed && (
            <button
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded-md transition-smooth ml-1",
                isCurrentActive
                  ? "text-foreground"
                  : "text-secondary-foreground"
              )}
              onClick={handleDropdownToggle}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}
        </div>

        {/* Sub-navigation items (expanded sidebar) */}
        {hasSubItems && isExpanded && !collapsed && (
          <ul className="ml-8 mt-2 space-y-1">
            {allowedSubItems.map((subItem) => (
              <li key={subItem.href}>
                <Link
                  to={subItem.href}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-xs font-medium transition-smooth",
                    location.pathname === subItem.href
                      ? "text-primary-foreground bg-primary"
                      : "text-secondary-foreground hover:bg-secondary"
                  )}
                  onClick={() => { }} // Don't auto-close on mobile
                >
                  <span className="flex-shrink-0">{subItem.icon}</span>
                  <span>{subItem.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {/* Collapsed sidebar sub-items (works for both mobile and desktop) */}
        {collapsed && hasSubItems && isExpanded && (
          <ul className="mt-2 space-y-1 bg-black/20 dark:bg-white/10 rounded-md p-2">
            {/* Add main item as first option in collapsed dropdown */}
            <li>
              <Link
                to={href}
                className={cn(
                  "flex items-center gap-2 px-2 py-2 rounded-md transition-smooth text-xs font-medium border-b border-white/10",
                  active
                    ? "text-primary-foreground bg-primary"
                    : "text-secondary-foreground hover:bg-secondary"
                )}
                onClick={() => { }} // Don't auto-close on mobile
                title={`${label} - Main`}
              >
                <span className="flex-shrink-0">{icon}</span>
                
              </Link>
            </li>
            {allowedSubItems.map((subItem) => (
              <li key={subItem.href}>
                <Link
                  to={subItem.href}
                  className={cn(
                    "flex items-center gap-2 px-2 py-2 rounded-md transition-smooth text-xs font-medium",
                    location.pathname === subItem.href
                      ? "text-primary-foreground bg-primary"
                      : "text-secondary-foreground hover:bg-secondary"
                  )}
                  onClick={() => { }} // Don't auto-close on mobile
                  title={subItem.label}
                >
                  <span className="flex-shrink-0">{subItem.icon}</span>
                  
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

// Navigation configuration with role-based access and optional sub-navigation
const navigationConfig: NavigationItem[] = [
  {
    icon: <Activity className="h-5 w-5" />,
    label: "Overview",
    href: "/overview",
    allowedRoles: ["admin", "executive", "wholesale", "ecommerce", "retail"] // Everyone can see overview
  },
  {
    icon: <ChartCandlestick className="h-5 w-5" />,
    label: "E-Commerce",
    href: "/ecommerce",
    allowedRoles: ["admin", "executive", "ecommerce"],
    subItems: [
      {
        icon: <Handshake className="h-4 w-4" />,
        label: "Marketing",
        href: "/ecomm-marketing",
        allowedRoles: ["admin", "executive", "ecommerce"]
      },
      {
        icon: <ShoppingCart className="h-4 w-4" />,
        label: "Sales",
        href: "/ecomm-sales",
        allowedRoles: ["admin", "executive", "ecommerce"]
      },
      {
        icon: <Users className="h-4 w-4" />,
        label: "Customer",
        href: "/ecomm-customer",
        allowedRoles: ["admin", "executive", "ecommerce"]
      }
    ]
  },
  {
    icon: <PackageOpen className="h-5 w-5" />,
    label: "Wholesale",
    href: "/wholesale",
    allowedRoles: ["admin", "executive", "wholesale"],
    subItems: [
      {
        icon: <Handshake className="h-4 w-4" />,
        label: "Marketing",
        href: "/wholesale-marketing",
        allowedRoles: ["admin", "executive", "wholesale"]
      },
      {
        icon: <ShoppingCart className="h-4 w-4" />,
        label: "Sales",
        href: "/wholesale-sales",
        allowedRoles: ["admin", "executive", "wholesale"]
      },
      {
        icon: <Users className="h-4 w-4" />,
        label: "Customer",
        href: "/wholesale-customer",
        allowedRoles: ["admin", "executive", "wholesale"]
      }
    ]
  },
  {
    icon: <Store className="h-5 w-5" />,
    label: "Retail",
    href: "/retail",
    allowedRoles: ["admin", "executive", "retail"],
    subItems: [
        {
        icon: <Handshake className="h-4 w-4" />,
        label: "Marketing",
        href: "/retail-marketing",
        allowedRoles: ["admin", "executive", "retail"]
      },
      {
        icon: <ShoppingCart className="h-4 w-4" />,
        label: "Sales",
        href: "/retail-sales",
        allowedRoles: ["admin", "executive", "retail"]
      },
      {
        icon: <Users className="h-4 w-4" />,
        label: "Customer",
        href: "/retail-customer",
        allowedRoles: ["admin", "executive", "retail"]
      }
    ]
  },
  {
    icon: <CalendarRange className="h-5 w-5" />,
    label: "Events",
    href: "/key-events",
    allowedRoles: ["admin", "executive", "wholesale", "ecommerce", "retail"]
  },
  {
    icon: <Upload className="h-5 w-5" />,
    label: "Upload",
    href: "/upload",
    allowedRoles: ["admin", "executive"]
  },
  {
    icon: <Settings className="h-5 w-5" />,
    label: "Settings",
    href: "/settings",
    allowedRoles: ["admin", "executive"] 
  }
];

// Helper function to check if user has access to a route
const hasRouteAccess = (userGroups: string[], allowedRoles: string[]): boolean => {
  if (!userGroups || userGroups.length === 0) return false;

  // Check if any of the user's groups match the allowed roles
  return userGroups.some(group =>
    allowedRoles.includes(group.toLowerCase())
  );
};

// Helper function to get filtered navigation items based on user role
const getFilteredNavigation = (userGroups: string[]): NavigationItem[] => {
  return navigationConfig.filter(item =>
    hasRouteAccess(userGroups, item.allowedRoles)
  );
};

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem("sidebar-collapsed");
    return stored === "true";
  });
  const { isMobile, isOpen, closeMenu } = useMobileMenu();
  const [sbImage, setBgImage] = useState("");
  const pathname = useLocation().pathname;

  const routeBackgroundMap: Record<string, string> = {
    // Default
    "/": "/images/background_boats.webp",
    //Ecommerce
    "/ecommerce": "/images/background_surf.webp",
    "/ecomm-marketing": "/images/background_home.webp",
    "/ecomm-sales": "/images/background_waves.webp",
    "/ecomm-customer": "/images/background_car.webp",
    // Wholesale
    "/wholesale": "/images/background_concrete.webp",
    "/wholesale-marketing": "/images/background_waves.webp",
    "/wholesale-sales": "/images/background_surf.webp",
    "/wholesale-customer": "/images/background_car.webp",
    // Retail
    "/retail": "/images/background_home.webp",
    "/retail-marketing": "/images/background_surf.webp",
    "/retail-sales": "/images/background_concrete.webp",
    "/retail-customer": "/images/background_waves.webp",
    // Events
    "/key-events": "/images/background_leaves.webp",
    // Upload
    "/upload": "/images/background_waves.webp",
    // Settings
    "/settings": "/images/background_concrete.webp",
  };

  const bgImage =
    routeBackgroundMap[pathname] ?? "/images/background_boats.webp";

  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  const user = getUserInfo();
  const userName = user?.username || '';
  const userGroups = user?.groups || [];

  // Get filtered navigation based on user's groups
  const allowedNavItems = getFilteredNavigation(userGroups);
  const [expandedNav, setExpandedNav] = useState<string | null>(null);

  useEffect(() => {
    const updateBg = () => {
      const darkMode = document.documentElement.classList.contains("dark");
      const img = darkMode
        ? "/images/alohadark.webp"
        : "/images/alohalight.webp";
      setBgImage(img);
    };

    updateBg();

    const observer = new MutationObserver(updateBg);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, closeMenu]);

  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", collapsed.toString());
  }, [collapsed]);

  useEffect(() => {
    // Find the parent item of the current route
    const parent = allowedNavItems.find(item =>
      item.subItems?.some(sub => sub.href === pathname)
    );

    // If no dropdown is currently expanded, auto-expand the parent
    if (parent && expandedNav === null) {
      setExpandedNav(parent.href);
    }
  }, [pathname, allowedNavItems, expandedNav]);

  return (
    <div className="flex h-screen w-full bg-zinc-50 dark:bg-zinc-950">
      {isMobile && isOpen && (
        <div className="flex-shrink-0 h-full">
          <MobileSidebar />
        </div>
      )}

      {/* Sidebar */}
      {!isMobile && (
        <div
          className={cn(
            "fixed inset-y-0 left-0 z-50 flex h-full flex-col align-center bg-white dark:bg-black lg:relative text-white bg-cover bg-center shadow-xl-soft select-none",
            "transition-[width] duration-300 ease-in-out",
            collapsed ? "w-[5.5em]" : "w-56"
          )}
          style={{
            backgroundImage: `url(${sbImage})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
        >
          <div className="flex h-auto items-center justify-between p-8 overflow-hidden">
            {!collapsed && !isMobile && (
              <div className="flex items-center gap-2">
                <TenoreLogo className="text-zinc-900 dark:text-white" />
              </div>
            )}
            {((collapsed && !isMobile) || (isMobile && !isOpen)) && (
              <div className="flex justify-center items-center w-full">
                <TenoreT className="text-black dark:text-white" />
              </div>
            )}
            {isMobile && isOpen && (
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <TenoreLogo className="text-zinc-900 dark:text-white" />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 rounded-full bg-black dark:bg-white dark:text-black"
                  onClick={closeMenu}
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close menu</span>
                </Button>
              </div>
            )}
            {!isMobile && (
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "absolute right-0 top-6 h-10 w-10 rounded-full bg-black dark:bg-white dark:text-black",
                  collapsed ? "-right-5" : "-right-5"
                )}
                onClick={() => setCollapsed(!collapsed)}
              >
                <ChevronLeft
                  className={cn(
                    "h-4 w-4 transition-all",
                    collapsed && "rotate-180"
                  )}
                />
                <span className="sr-only">Toggle sidebar</span>
              </Button>
            )}
          </div>
          {isMobile ? (
            <div className="flex-1 overflow-y-auto">
              <nav className="flex-1 flex flex-col overflow-y-auto p-5 scroll-smooth overscroll-contain">
                <ul className="space-y-1">
                  {allowedNavItems.map((item) => (
                    <NavItem
                      key={item.href}
                      icon={item.icon}
                      label={item.label}
                      href={item.href}
                      collapsed={collapsed && !isMobile}
                      active={pathname === item.href}
                      onClick={() => { }} // Don't close menu on mobile nav clicks
                      subItems={item.subItems}
                      userGroups={userGroups}
                      expandedNav={expandedNav}
                      setExpandedNav={setExpandedNav}
                    />
                  ))}
                </ul>
              </nav>
            </div>
          ) : (
            <nav className="flex-1 flex flex-col overflow-y-auto p-5 scroll-smooth overscroll-contain">
              <ul className="space-y-1">
                {allowedNavItems.map((item) => (
                  <NavItem
                    key={item.href}
                    icon={item.icon}
                    label={item.label}
                    href={item.href}
                    collapsed={collapsed && !isMobile}
                    active={pathname === item.href}
                    onClick={isMobile ? () => { } : closeMenu} // Don't close on mobile, do close on desktop
                    subItems={item.subItems}
                    userGroups={userGroups}
                    expandedNav={expandedNav}
                    setExpandedNav={setExpandedNav}
                  />
                ))}
              </ul>
            </nav>
          )}

          <div className="p-6 overflow-hidden flex items-center justify-between gap-2">
            {(collapsed && !isMobile) ? (
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="icon"
                className="text-black dark:text-white flex-shrink-0 mx-auto"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            ) : (
              <div className="w-full flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <CircleUserRound className="h-10 w-10 text-foreground" />
                  <div className="flex flex-col max-w-[80px]">
                    <span className="text-xs uppercase font-bold text-black dark:text-white tracking-[.1em] truncate">
                      {userName}
                    </span>
                    <span className="text-xs text-black/50 dark:text-white/50">
                      {Array.isArray(userGroups) ? userGroups.join(', ') : userGroups}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  size="icon"
                  className="text-black dark:text-white flex-shrink-0"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {isMobile && <MobileSidebar />}
      <main className={cn(
        "relative flex flex-1 flex-col h-[100dvh] overflow-y-auto",
        isMobile ? "pl-0" : "pl-16 lg:pl-5"
      )}>
        <div
          className="fixed inset-0 bg-top bg-cover transition-all duration-300 z-0"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="fixed inset-0 bg-white/10 dark:bg-black/50 z-1" />

        <header className="flex h-16 items-center justify-end sm:p-3 z-10">

          <div className="flex items-center gap-2">
            <div className="flex text-white text-sm mr-2 items-center">
              <div className="w-5 h-5 rounded-full border-4 border-green-500/40 drop-shadow-[0_0_6px_rgba(74,222,128,1)] mr-2">
                <div className="w-full h-full bg-gradient-to-t from-green-500 to-lime-400 rounded-full border-2 border-green-200"></div>
              </div>
              Connected to&nbsp;<span className="font-bold">LUCIDA</span>
            </div>
            <NotificationsDropdown />
            <ThemeToggle />
          </div>
        </header>

        <div className="p-4 sm:px-10 sm:py-10 z-40">{children}</div>

        <div className="text-3xl text-center uppercase font-light tracking-[.1em] text-white/80 pt-24 pb-32 bg-gradient-to-b from-black/0 to-black/70 select-none z-40">
          Highest of the Ordinary
        </div>
      </main>

    </div>
  );
}